/**
 * Program 12.1 UX2 Wave 1 — Commercial Pursuit Workspace shell.
 * Aggregation over Opportunity + ECLE; no new pursuit entity.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Drawer,
  Grid,
  Row,
  Space,
  Spin,
  Typography,
  Button,
} from 'antd';
import { ArrowLeftOutlined, BulbOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import opportunityService from '@/services/opportunity.service';
import { commercialLifecycleService } from '@/services/commercialLifecycle.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import { CommercialLifecycleRail } from '@/components/epc/commercial-ux/CommercialLifecycleRail';
import { PursuitHeader } from '@/components/epc/commercial-ux/PursuitHeader';
import { PursuitAttentionPanel } from '@/components/epc/commercial-ux/PursuitAttentionPanel';
import { PursuitNextActions } from '@/components/epc/commercial-ux/PursuitNextActions';
import { PursuitActivityTimeline } from '@/components/epc/commercial-ux/PursuitActivityTimeline';
import { EpcPursuitBriefPanel } from '@/components/epc/commercial-ux/EpcPursuitBriefPanel';
import { EpcCommercialIntelligenceCompact } from '@/components/epc/commercial-ux/EpcCommercialIntelligenceCompact';
import {
  buildPursuitViewModel,
  stageFocusPath,
} from '@/components/epc/commercial-ux/pursuitWorkspaceModel';
import type { LifecycleStageView } from '@/components/commercial-lifecycle/lifecycleStageView';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const CommercialPursuitWorkspacePage: React.FC = () => {
  const { opportunityId = '' } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isTablet = !screens.lg;
  const [intelOpen, setIntelOpen] = useState(false);
  const [focusStageKey, setFocusStageKey] = useState<string | null>(null);

  const oppQ = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => opportunityService.getById(opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 15_000,
  });

  const ctxQ = useQuery({
    queryKey: ['commercial-lifecycle-context', 'opportunity', opportunityId],
    queryFn: () => commercialLifecycleService.getContext('opportunity', opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 30_000,
    retry: false,
  });

  const defsQ = useQuery({
    queryKey: ['commercial-lifecycle-definitions'],
    queryFn: () => commercialLifecycleService.getDefinitions(),
    enabled: Boolean(ctxQ.data?.lifecycle?.definitionCode),
    staleTime: 60_000,
    retry: false,
  });

  const tendersQ = useQuery({
    queryKey: ['epc-tenders', opportunityId],
    queryFn: () => epcCommercialService.listTenders(opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 15_000,
    retry: false,
  });

  const prequalsQ = useQuery({
    queryKey: ['epc-prequalifications', opportunityId],
    queryFn: () => epcCommercialService.listPrequalifications(opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 15_000,
    retry: false,
  });

  const vm = useMemo(() => {
    const code = ctxQ.data?.lifecycle?.definitionCode;
    const def = (defsQ.data || []).find((d) => d.code === code);
    return buildPursuitViewModel({
      opportunityId,
      opportunity: oppQ.data || null,
      context: ctxQ.data || null,
      definition: def,
      tenders: (tendersQ.data || []) as Record<string, unknown>[],
      contracts: [],
      prequals: (prequalsQ.data || []) as Record<string, unknown>[],
    });
  }, [opportunityId, oppQ.data, ctxQ.data, defsQ.data, tendersQ.data, prequalsQ.data]);

  const primaryReady = vm.nextActions.find((a) => !a.blocked);
  const primaryBlocked = !primaryReady ? vm.nextActions.find((a) => a.blocked) : undefined;

  const goStage = (stage: LifecycleStageView) => {
    setFocusStageKey(stage.stageKey);
    const path = stageFocusPath(stage.stageKey, vm);
    if (path) navigate(path);
  };

  const intelligenceCard = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small" title="Needs attention">
        <PursuitAttentionPanel
          items={vm.attention}
          onItemClick={(item) => {
            if (item.stageKey) {
              const stage = vm.stages.find((s) => s.stageKey === item.stageKey);
              if (stage) goStage(stage);
            }
          }}
        />
      </Card>
      <Card size="small" title="Next best actions">
        <PursuitNextActions
          actions={vm.nextActions}
          onSelect={(a) => {
            const stage = vm.stages.find((s) => s.stageKey === a.toStageKey);
            if (stage) goStage(stage);
            else if (a.toStageKey.includes('CONTRACT') && vm.primaryContractId) {
              navigate(`/epc/commercial/contracts/${vm.primaryContractId}`);
            } else if (a.toStageKey.includes('TENDER') && vm.primaryTenderId) {
              navigate(`/epc/commercial/tenders/${vm.primaryTenderId}`);
            }
          }}
        />
      </Card>
      <Card size="small" title="Lineage">
        <PursuitActivityTimeline
          nodes={vm.lineage}
          onNodeClick={(n) => {
            const stage = vm.stages.find((s) => s.stageKey === n.stageKey);
            if (stage) goStage(stage);
          }}
        />
      </Card>
      <EpcPursuitBriefPanel vm={vm} />
      <EpcCommercialIntelligenceCompact
        memoryQueryHint={vm.commercialSummary.name}
        title="EOIP / Memory (advisory)"
      />
      <Alert
        type="info"
        showIcon
        message="Universal Intelligence sidebar"
        description="Toolbar Intelligence still opens EOIP / Memory / Explain for page context. Cards above are pursuit-scoped and advisory only."
      />
    </Space>
  );

  if (!opportunityId) {
    return <Alert type="error" message="Missing opportunity id" />;
  }

  if (oppQ.isLoading || ctxQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Loading pursuit…</Text>
        </div>
      </div>
    );
  }

  if (oppQ.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Unable to load opportunity"
          description={(oppQ.error as Error)?.message}
          action={
            <Button onClick={() => oppQ.refetch()} aria-label="Retry load opportunity">
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: isTablet ? 16 : 24 }}>
      <AiPageContextSync module="epc" docType="commercial_pursuit" docId={opportunityId} />

      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/epc/commercial')}
          aria-label="Back to commercial hub"
        >
          Commercial
        </Button>
        <Link to={`/growth/opportunities/${opportunityId}`}>Opportunity detail</Link>
        {vm.primaryTenderId ? (
          <Link to={`/epc/commercial/tenders/${vm.primaryTenderId}`}>Tender workspace</Link>
        ) : null}
        {vm.primaryContractId ? (
          <Link to={`/epc/commercial/contracts/${vm.primaryContractId}`}>
            Contract administration
          </Link>
        ) : null}
        {isTablet ? (
          <Button
            icon={<BulbOutlined />}
            onClick={() => setIntelOpen(true)}
            aria-label="Open attention panel"
          >
            Attention
          </Button>
        ) : null}
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <PursuitHeader
          vm={vm}
          primaryActionLabel={primaryReady?.label || primaryBlocked?.label || null}
          primaryActionDisabled={Boolean(primaryBlocked && !primaryReady)}
          primaryActionReason={
            primaryBlocked && !primaryReady
              ? primaryBlocked.reason || 'Action blocked by lifecycle gates'
              : undefined
          }
          onPrimaryAction={() => {
            const a = primaryReady || primaryBlocked;
            if (!a) return;
            const stage = vm.stages.find((s) => s.stageKey === a.toStageKey);
            if (stage) goStage(stage);
          }}
          secondaryLinks={[
            ...(vm.primaryTenderId
              ? [
                  {
                    label: 'Open tender',
                    onClick: () => navigate(`/epc/commercial/tenders/${vm.primaryTenderId}`),
                  },
                ]
              : []),
            ...(vm.primaryContractId
              ? [
                  {
                    label: 'Contract admin',
                    onClick: () => navigate(`/epc/commercial/contracts/${vm.primaryContractId}`),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <CommercialLifecycleRail
          stages={vm.stages}
          activeStageKey={focusStageKey || vm.context?.currentStage?.stageKey}
          onStageClick={goStage}
          progressPercent={vm.context?.progress?.percent}
          compact={isTablet}
        />
        {ctxQ.isError ? (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Lifecycle context unavailable"
            description="Pursuit header still works. Retry when the network recovers."
          />
        ) : null}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Stage workspace" style={{ minHeight: 280 }}>
            <Title level={5} style={{ marginTop: 0 }}>
              {vm.commercialSummary.stageLabel}
            </Title>
            <Paragraph type="secondary">
              Wave 1 overview: stage cockpits land in later UX2 waves. Use the rail and links to open
              existing authoritative modules (tender, bid, award, contract admin, mobilization).
            </Paragraph>
            <Space direction="vertical" size="small">
              <Text>
                Progress: {vm.context?.progress?.completedStages ?? 0} /{' '}
                {vm.context?.progress?.totalStages ?? 0} stages
              </Text>
              <Text type="secondary">
                Focused stage: {focusStageKey || vm.context?.currentStage?.stageKey || '—'}
              </Text>
              {vm.primaryTenderId ? (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`/epc/commercial/tenders/${vm.primaryTenderId}`)}
                >
                  Continue in tender workspace
                </Button>
              ) : (
                <Text type="secondary">No tender linked yet for this opportunity.</Text>
              )}
            </Space>
          </Card>
        </Col>
        {!isTablet ? (
          <Col xs={24} lg={8}>
            {intelligenceCard}
          </Col>
        ) : null}
      </Row>

      <Drawer
        title="Attention & next actions"
        open={intelOpen}
        onClose={() => setIntelOpen(false)}
        width={Math.min(420, typeof window !== 'undefined' ? window.innerWidth - 24 : 420)}
        destroyOnClose
      >
        {intelligenceCard}
      </Drawer>
    </div>
  );
};

export default CommercialPursuitWorkspacePage;
